import { useMutation, useQuery, useQueryClient } from 'react-query';
import axiosInstance from '../config/Axios';
import { useCustomToast } from '../pages/Contexts/ToastContext';
import { onUpgrade402 } from '../utils/onUpgrade402';

/**
 * AI Promo Designer (Phase 2B) — generate a finished promo poster from a one-line
 * promotion, save the chosen one to the media library + Promotions gallery.
 * Mirrors queries/dishImages.js. Resource-wrapped responses unwrap via res?.data.
 */
const unwrap = (res) => res?.data ?? res;

const PROMOS_KEY = 'promosGallery';

export const useGeneratePromo = () => {
  const { showToast } = useCustomToast();
  return useMutation(
    async (payload) => unwrap((await axiosInstance.post('/promos/generate', payload)).data),
    {
      onError: (err) => {
        if (onUpgrade402(err, showToast)) return;
        showToast('error', 'AI Promo Designer', err.response?.data?.message);
      },
    }
  );
};

export const useSavePromo = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation(
    async ({ token, name }) => unwrap((await axiosInstance.post('/promos/save', { token, name })).data),
    {
      onSuccess: () => {
        qc.invalidateQueries(PROMOS_KEY);
        qc.invalidateQueries('videoWallMediaLibrary');
      },
      onError: (err) => showToast('error', 'AI Promo Designer', err.response?.data?.message),
    }
  );
};

export const useGenerateMenuHighlight = () => {
  const { showToast } = useCustomToast();
  return useMutation(
    async (payload) => unwrap((await axiosInstance.post('/promos/menu-highlight', payload)).data),
    {
      onError: (err) => {
        if (onUpgrade402(err, showToast)) return;
        showToast('error', 'AI Promo Designer', err.response?.data?.message);
      },
    }
  );
};

export const useGetPromos = (enabled = true) =>
  useQuery(
    PROMOS_KEY,
    async () => {
      const res = unwrap((await axiosInstance.get('/promos')).data);
      return res?.data ?? res ?? [];
    },
    { enabled, staleTime: 15_000 }
  );

export const useDeletePromo = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation(
    async (id) => unwrap((await axiosInstance.delete(`/promos/${id}`)).data),
    {
      onSuccess: () => { qc.invalidateQueries(PROMOS_KEY); showToast('success', 'AI Promo Designer', 'Promo removed.'); },
      onError: (err) => showToast('error', 'AI Promo Designer', err.response?.data?.message),
    }
  );
};
